<script setup lang="ts">
import type { LocationQueryRaw } from "vue-router"
import type { Album } from "@/models"
import cover from "@brand-cover"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted, ref, useTemplateRef, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { AlbumSelectorTitle } from "@/components"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { Button, Icon, Tooltip } from "@/components/ui"
import { useTranslation } from "@/composables/useTranslation"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { AlbumIdEventBus } from "../Nav/AlbumIdEventBus"

interface AlbumSelectorProps {
  albums?: Album[]
  eventCoverImage?: string
  modelValue?: number
  hideMainGallery?: boolean
  isLoading?: boolean
}

const props = withDefaults(defineProps<AlbumSelectorProps>(), {
  albums: () => [],
  eventCoverImage: "",
  hideMainGallery: false,
  isLoading: false,
})

const emit = defineEmits<{
  "update:modelValue": [value?: number]
  "select": [album: Album]
}>()

const { t } = useTranslation()
const router = useRouter()

const albumSelectorContainer = useTemplateRef<HTMLDivElement | null>("albumSelectorContainer")
const createdAlbumsContainer = useTemplateRef<HTMLDivElement | null>("createdAlbumsContainer")

const eventVaultStore = useEventVaultStore()

const revealedAlbumId = ref<number>()

const params = useUrlSearchParams<{ album?: unknown }>()

onMounted(() => {
  if (params.album && typeof params.album === "string") {
    const albumId = Number(params.album)
    emit("update:modelValue", albumId)
    eventVaultStore.setAlbumId(albumId)
  }
})

const hasScrolledOnLoad = ref<boolean>(false)

watchEffect(() => {
  if (hasScrolledOnLoad.value)
    return

  const selectedAlbum = createdAlbumsContainer.value?.querySelector(`[data-id="album-${props.modelValue}"]`)
  if (selectedAlbum) {
    selectedAlbum.scrollIntoView({ behavior: "smooth", block: "center" })
    hasScrolledOnLoad.value = true
  }
})

function onTitleOpenChange(open: boolean, albumId?: number) {
  revealedAlbumId.value = open ? albumId : undefined
}

function onAlbumClick(album: Album) {
  revealedAlbumId.value = undefined

  if (album.id) {
    params.album = album.id.toString()
  }

  emit("update:modelValue", album.id)
  emit("select", album)
  emitSelectedAlbum(album.id)
  eventVaultStore.setAlbumId(album.id)
}

function removeAlbumParam() {
  delete params.album
  router.replace({ query: params as LocationQueryRaw })
  AlbumIdEventBus.emit(undefined)
  eventVaultStore.setAlbumId(undefined)
}

function onAlbumClear() {
  revealedAlbumId.value = undefined
  removeAlbumParam()
  emit("update:modelValue", undefined)
  eventVaultStore.setAlbumId(undefined)
}

function slideRight() {
  albumSelectorContainer.value?.scrollBy({ left: 200, behavior: "smooth" })
}

const isScrollable = computed(() => {
  return albumSelectorContainer.value
    ? albumSelectorContainer.value.scrollWidth > albumSelectorContainer.value.clientWidth
    : false
})
</script>

<template>
  <div v-if="albums?.length" tabindex="-1" class="gpx-album-selector-wrapper">
    <div class="gpx-album-selector-info_desktop z-10">
      <div class="gpx-album-selector-info-header_desktop mb-3">
        <Icon icon="Folder" size="lg" />
        <h2 class="text-xl">
          {{ t("albums") }}
        </h2>
      </div>
      <p>{{ t("album_selector.select_album") }}</p>
    </div>
    <div ref="albumSelectorContainer" class="gpx-album-selector">
      <div v-if="!hideMainGallery" class="gpx-album-selector-tile_clear rounded-xl">
        <button
          data-test="gpx-album-selector-clear" class="gpx-album-selector-tile-wrapper rounded-xl"
          :class="{ 'gpx-album-selector-tile-wrapper_active': !modelValue }" @click="onAlbumClear"
        >
          <span class="gpx-album-selector-tile" :class="{ 'gpx-album-selector-tile_active': !modelValue }">
            <span v-show="!modelValue" class="gpx-album-selector-overlay z-10">
              <span>{{ t("album_selector.selected") }}</span>
            </span>
            <img
              v-if="eventCoverImage" :src="eventCoverImage ?? cover"
              :alt="t('album_selector.main_cover_image_alt')"
            >
            <img
              v-else :src="cover"
              :alt="t('album_selector.main_cover_image_alt')"
            >
          </span>
          <span class="gpx-album-selector-title" :class="{ 'gpx-album-selector-title_active': !modelValue }">
            <Icon icon="Folder" />{{ t("album_selector.main_gallery") }}
          </span>
        </button>
      </div>
      <div>
        <transition mode="out-in" name="gpx-fade">
          <div v-if="isLoading" class="gpx-album-selector-items">
            <div
              v-for="n in 5" :key="`skeleton-${n}`"
              class="gpx-album-selector-tile-wrapper rounded-xl"
            >
              <div class="gpx-skeleton gpx-album-selector-tile" />
            </div>
          </div>
          <div v-else ref="createdAlbumsContainer" class="gpx-album-selector-items">
            <button
              v-for="(album, index) in albums" :key="`album-${index}`" :data-id="`album-${album.id}`"
              class="gpx-album-selector-tile-wrapper rounded-xl"
              :class="{ 'gpx-album-selector-tile-wrapper_active': modelValue === album.id }"
              @click.exact="() => onAlbumClick(album)"
            >
              <span
                class="gpx-album-selector-tile"
                :class="{ 'gpx-album-selector-tile_active': modelValue === album.id }"
              >
                <span v-show="modelValue === album.id" class="gpx-album-selector-overlay">
                  <span>{{ t("album_selector.selected") }}</span>
                </span>
                <img
                  v-if="album.coverImageUrl" :src="album.coverImageUrl"
                  :alt="t('album_selector.cover_image_alt')"
                >
              </span>
              <span>
                <span
                  class="gpx-album-selector-title"
                  :class="{ 'gpx-album-selector-title_active': modelValue === album.id }"
                >
                  <Tooltip v-if="album.isLocked">
                    <template #content>
                      {{ t('album_selector.locked_tooltip') }}
                    </template>
                    <Icon icon="Lock" />
                  </Tooltip>
                  <Tooltip v-else-if="album.isHidden">
                    <template #content>
                      {{ t('album_selector.hidden_tooltip') }}
                    </template>
                    <Icon icon="EyeOff" />
                  </Tooltip>
                  <Icon v-else icon="Folder" style="align-self: flex-start" />
                  <AlbumSelectorTitle
                    :title="album.title ?? ''"
                    :popover-open="revealedAlbumId === album.id"
                    @update:popover-open="(open: boolean) => onTitleOpenChange(open, album.id)"
                  />
                </span>
              </span>
            </button>
          </div>
        </transition>
      </div>
    </div>
    <div class="gpx-album-selector-info_mobile">
      <div class="gpx-album-selector-info-hint_mobile">
        <Icon icon="CircleInfo" />
        <p>{{ t("album_selector.select_album") }}</p>
      </div>
      <Button v-if="isScrollable" variant="flat" @click="slideRight">
        <p>{{ t("album_selector.scroll") }}</p>
        <Icon icon="ArrowRight" />
      </Button>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css";

h2 {
  margin: 0;
}

.gpx-album-selector-wrapper {
  @apply bg-gray-200;

  padding: 1rem 1rem 0;
}

.gpx-album-selector {
  @apply flex flex-row gap-5 overflow-x-auto;
}

.gpx-album-selector-info_desktop {
  @apply hidden;
}

.gpx-album-selector-info_mobile,
.gpx-album-selector-info-hint_mobile,
.gpx-album-selector-info-header_desktop {
  @apply flex items-center;
}

.gpx-album-selector-info-hint_mobile,
.gpx-album-selector-info-header_desktop {
  @apply w-full gap-2;
}

.gpx-album-selector-info-header_desktop {
  @apply text-primary;
}

.gpx-album-selector-info_mobile,
.gpx-album-selector-info_desktop p {
  @apply font-medium text-gray-700 font-montserrat;
}

a {
  text-decoration: none;
}

.gpx-album-selector-items {
  @apply flex flex-row gap-4;
}

.gpx-album-selector-tile_clear button,
.gpx-album-selector-items button {
  @apply border-none p-0 m-0;
}

.gpx-album-selector-tile_clear {
  @apply bg-secondary;
}

.gpx-album-selector-tile-wrapper {
  @apply bg-secondary overflow-hidden cursor-pointer max-w-min w-full;

  transition: 0.15s ease;
  flex: 1 1 auto;
}

.gpx-album-selector-items > .gpx-album-selector-tile-wrapper {
  flex: 1;
}

.gpx-album-selector-tile {
  @apply relative flex border-none items-center justify-center overflow-hidden;

  aspect-ratio: 9/10;
  min-width: 164px;
  width: 164px;
  transition: all 0.15s ease;
  filter: grayscale(1);
}

.gpx-album-selector-tile img {
  @apply object-cover h-full w-full;

  font-size: 0;
}

.gpx-album-selector-title {
  @apply flex p-1.5 gap-1.5 items-center font-montserrat font-normal text-sm text-secondary-card-text text-start;
}

.gpx-album-selector-title svg {
  @apply shrink-0 self-start;
}

.gpx-album-selector-title_active {
  @apply text-primary-card-text;
}

.gpx-album-selector-tile-wrapper_active {
  @apply bg-primary;
}

.gpx-album-selector-tile_active {
  filter: grayscale(0);
}

.gpx-album-selector-tile span {
  @apply flex gap-2 place-content-center text-base;

  transition: 0.15s ease;
}

.gpx-album-selector-overlay {
  @apply absolute inset-0 h-full flex items-center justify-center;
}

.gpx-album-selector-overlay span {
  @apply text-primary bg-white font-normal font-montserrat text-xs rounded-sm;

  padding: 0.25rem 1rem;
}

@media (width >=768px) {
  .gpx-album-selector-wrapper {
    width: fit-content;
    padding-top: 0;
    overflow-y: auto;
  }

  .gpx-album-selector {
    flex-direction: column;
  }

  .gpx-album-selector-info_desktop {
    display: block;
    position: sticky;
    top: 0;
    padding: 1rem 0;

    @apply bg-gray-200;
  }

  .gpx-album-selector-items {
    flex-direction: column;
    width: fit-content;
    overflow-y: auto;
    padding-bottom: 3rem;
  }

  .gpx-album-selector-info_mobile {
    display: none;
  }

  .gpx-album-selector-tile {
    aspect-ratio: 16 / 9;
    width: 280px;
    min-width: 280px;
  }

  .gpx-album-selector-tile span {
    font-size: 1rem;
    padding: 0.25rem 1.5rem;
  }
}
</style>
